import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import {
    Alert,
    Box,
    Typography,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    IconButton
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Warning as WarningIcon, ErrorOutline as ErrorOutlineIcon, SwapHoriz as SwapHorizIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getRoleUsers } from '../../redux/ducks/role';
import { updateUserRole } from '../../redux/slices/users';

const HelperText = styled(DialogContentText)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const LoadingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const EmptyState = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const ErrorState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    color: theme.palette.error.main,
}));

const FlagBanner = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'severity',
})(({ theme, severity }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette[severity].main}`,
    color: theme.palette[severity].main,
}));

const RangeNote = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const BlockedNote = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const UserRow = styled(TableRow, {
    shouldForwardProp: (prop) => prop !== 'tint',
})(({ theme, tint }) => ({
    backgroundColor: tint === 'none' ? 'transparent' : alpha(theme.palette[tint].light, 0.13),
}));

const NameCell = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
}));

const StateChips = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(0.5),
}));

const ChangeBlock = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const ChangeFact = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
}));

const FactLabel = styled(Typography)(({ theme }) => ({
    minWidth: theme.spacing(14),
    color: theme.palette.text.secondary,
}));

const plural = (count) => (count === 1 ? '' : 's');
const pluralVerb = (count) => (count === 1 ? 's' : '');

const listLabels = (labels, max = 3) => {
    if (labels.length === 0) return '';
    const shown = labels.slice(0, max);
    const remaining = labels.length - shown.length;
    const text = shown.length === 1 ? shown[0] : `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`;
    if (remaining === 0) return text;
    return `${text}, plus ${remaining} more sensitive capabilit${remaining === 1 ? 'y' : 'ies'}`;
};

const fullName = (user) => `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email || 'this user';

// Only the client decides whether the audit flags a holder, so the copy about a proposed role has to
// read the same way the server-side flagging does rather than assuming everyone here is external.
const isExternalHolder = (user) => user?.client?.isInternal !== true;

// The server refuses an Internal-typed role for anyone whose client is not internal, so offering one
// here would be a guaranteed rejection on exactly the rows this tool exists to correct.
const roleAllowedForHolder = (option, user) => option?.type !== 'Internal' || !isExternalHolder(user);

const RoleUsersDialog = ({ open, onClose, role, onRoleChanged }) => {
    const dispatch = useDispatch();
    const currentUserId = useSelector((state) => state.auth.userId);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [data, setData] = useState(null);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(25);
    // Kept after close so the confirmation does not blank its own text mid exit-transition.
    const [changeTarget, setChangeTarget] = useState(null);
    const [changeOpen, setChangeOpen] = useState(false);
    const [newRoleId, setNewRoleId] = useState('');
    const [changing, setChanging] = useState(false);
    const [changeError, setChangeError] = useState(null);

    const total = data?.total || 0;
    // A role change removes a holder, so `total` shrinks under the current page — clamp before asking.
    const lastPage = total > 0 ? Math.ceil(total / limit) - 1 : 0;
    const effectivePage = Math.min(page, lastPage);

    const loadUsers = useCallback(() => {
        if (!role?._id) return;

        setLoading(true);
        setFailed(false);
        dispatch(getRoleUsers(role._id, { start: effectivePage * limit, limit }, (response) => {
            setData(response || null);
            setFailed(!response);
            setLoading(false);
        }));
    }, [dispatch, role?._id, effectivePage, limit]);

    useEffect(() => {
        if (!open) return;
        loadUsers();
    }, [open, loadUsers]);

    useEffect(() => {
        if (page !== effectivePage) setPage(effectivePage);
    }, [page, effectivePage]);

    const users = data?.users || [];
    const roleType = data?.role?.type || role?.type;
    const exposedCapabilities = data?.role?.exposedCapabilities || [];
    const capabilityLabels = exposedCapabilities.map((capability) => capability.label);
    const primaryCapability = capabilityLabels[0];
    // Named by the server from the same list the check ran with, so the copy cannot drift narrower than the audit.
    const auditScopeLabels = data?.auditScope || [];
    const auditScope = auditScopeLabels.length > 6
        ? `${auditScopeLabels.slice(0, 6).join(', ')}, plus ${auditScopeLabels.length - 6} more`
        : listLabels(auditScopeLabels, auditScopeLabels.length);
    const auditScopeSuffix = auditScope ? ` (${auditScope})` : '';
    const flag = data?.flag || null;
    const flaggedActive = data?.flaggedActive || 0;
    const flaggedInactive = data?.flaggedInactive || 0;
    const flaggedDeleted = data?.flaggedDeleted || 0;
    const flaggedTotal = data?.flaggedTotal || 0;
    const activeTotal = data?.activeTotal || 0;
    const inactiveTotal = data?.inactiveTotal || 0;
    const deletedTotal = data?.deletedTotal || 0;
    const roleChange = data?.roleChange || null;
    const assignableRoles = useMemo(() => roleChange?.assignableRoles || [], [roleChange]);
    const start = data?.start || 0;

    const selectedRole = assignableRoles.find((option) => option._id === newRoleId) || null;
    const blockedForTarget = assignableRoles.some((option) => !roleAllowedForHolder(option, changeTarget));

    const accessLine = capabilityLabels.length > 0
        ? roleType === 'Internal'
            ? `This role is typed Internal and grants access to ${listLabels(capabilityLabels)}.`
            : `This role is typed General but grants access to ${listLabels(capabilityLabels)}.`
        : roleType === 'Internal'
            ? `This role is typed Internal but grants none of the sensitive capabilities this audit checks for${auditScopeSuffix}.`
            : `This role grants none of the sensitive capabilities this audit checks for${auditScopeSuffix}.`;

    const rowTint = (user) => {
        if (user.flag === 'exposure' && user.active && !user.isDeleted) return 'error';
        if (user.flag) return 'warning';
        return 'none';
    };

    const openChangeDialog = (user) => {
        setChangeTarget(user);
        setNewRoleId('');
        setChangeError(null);
        setChangeOpen(true);
    };

    const handleConfirmChange = () => {
        if (!changeTarget || !newRoleId) return;
        setChanging(true);
        setChangeError(null);
        dispatch(updateUserRole({
            userId: changeTarget._id,
            roleId: newRoleId,
            callback: (response, error) => {
                setChanging(false);
                if (!response) {
                    setChangeError(error?.message || 'The role was not changed. Nothing about this account has been altered.');
                    return;
                }
                setChangeOpen(false);
                // Refetched rather than patched: the flag counts are computed over the whole role, so
                // dropping the row locally would leave the totals describing a set that no longer exists.
                loadUsers();
                if (onRoleChanged) onRoleChanged();
            },
        }));
    };

    const describeRole = (option) => {
        if (!option) return '';
        const kind = option.type === 'Internal'
            ? 'an internal role, meant for people who work for the company'
            : 'a client-facing role, meant for people who work for a client company';
        const caps = option.exposedCapabilities?.length > 0
            ? `It grants access to ${listLabels(option.exposedCapabilities.map((capability) => capability.label), 6)}.`
            : `It grants none of the sensitive capabilities this audit checks for${auditScopeSuffix}.`;
        return `"${option.name}" is ${kind}. ${caps}`;
    };

    const changeOutcome = () => {
        if (!changeTarget || !selectedRole || !isExternalHolder(changeTarget)) return null;

        const who = changeTarget.client
            ? `${fullName(changeTarget)} belongs to ${changeTarget.client.name}, which is an external client`
            : `${fullName(changeTarget)} has no client on record, which this audit treats the same as an external one`;

        if (selectedRole.exposedCapabilities?.length > 0) {
            return { severity: 'error', text: `${who}. This audit would flag the new assignment as an exposure — an external client's user holding a role that grants sensitive capabilities.` };
        }
        return { severity: 'success', text: `${who}. This audit would not flag the new assignment.` };
    };

    const isSelf = (user) => String(user?._id) === String(currentUserId);

    const changeButtonTooltip = (user) => {
        if (!roleChange?.allowed) return roleChange?.reason || 'You cannot change roles from here.';
        if (isSelf(user)) return 'This is your own account. Ask another administrator to change your role.';
        if (!user.canChangeRole) return `You can only change the role of people at your own client, and ${fullName(user)} is not.`;
        return `Change ${fullName(user)}'s role`;
    };

    const renderBanner = () => {
        if (!flag || (flaggedActive === 0 && flaggedInactive === 0 && flaggedDeleted === 0)) return null;

        const severity = flag === 'exposure' && flaggedActive > 0 ? 'error' : 'warning';
        const lines = [];

        if (flag === 'exposure') {
            if (flaggedActive > 0) {
                lines.push(`${flaggedActive} active user${plural(flaggedActive)} on this role belong${pluralVerb(flaggedActive)} to an external client, and this role grants access to ${listLabels(capabilityLabels)}. They have that access right now — change their role to fix this.`);
            }
            if (flaggedInactive > 0) {
                lines.push(`${flaggedInactive} deactivated account${plural(flaggedInactive)} on an external client also hold${pluralVerb(flaggedInactive)} this role. Deactivated accounts cannot sign in, so they are listed but not counted as live exposure — reactivating one would expose ${primaryCapability}.`);
            }
            if (flaggedDeleted > 0) {
                lines.push(`${flaggedDeleted} deleted account${plural(flaggedDeleted)} on an external client also hold${pluralVerb(flaggedDeleted)} this role. Deleted accounts cannot sign in, so they are listed but not counted as live exposure — restoring one would expose ${primaryCapability}.`);
            }
        } else {
            if (flaggedActive > 0) {
                lines.push(`${flaggedActive} active user${plural(flaggedActive)} on this role belong${pluralVerb(flaggedActive)} to an external client. This role is typed Internal but grants none of the sensitive capabilities this audit checks for${auditScopeSuffix}, so nothing is exposed by it — the assignment is still wrong and should be corrected.`);
            }
            if (flaggedInactive > 0) {
                lines.push(`${flaggedInactive} deactivated account${plural(flaggedInactive)} on an external client also hold${pluralVerb(flaggedInactive)} this role. They cannot sign in, so they are listed but not counted as live holders.`);
            }
            if (flaggedDeleted > 0) {
                lines.push(`${flaggedDeleted} deleted account${plural(flaggedDeleted)} on an external client also hold${pluralVerb(flaggedDeleted)} this role. They cannot sign in, so they are listed but not counted as live holders.`);
            }
        }

        return (
            <FlagBanner severity={severity}>
                <WarningIcon fontSize="small" />
                <Box>
                    {lines.map((line) => (
                        <Typography key={line} variant="body2">{line}</Typography>
                    ))}
                </Box>
            </FlagBanner>
        );
    };

    const renderFlagChip = (user) => {
        if (!user.flag) return null;

        if (user.flag === 'exposure') {
            const dormantReason = user.isDeleted ? 'deleted' : 'deactivated';
            const dormantAction = user.isDeleted ? 'restoring it' : 'reactivating it';

            if (user.isDeleted || !user.active) {
                return (
                    <Tooltip title={`This user's client is external and this role grants access to ${listLabels(capabilityLabels, 6)}. The account is ${dormantReason}, so it cannot sign in — but ${dormantAction} would expose ${primaryCapability}.`}>
                        <Chip
                            icon={<WarningIcon />}
                            label={user.isDeleted ? 'Would be exposed if restored' : 'Would be exposed if reactivated'}
                            size="small"
                            color="warning"
                            variant="outlined"
                        />
                    </Tooltip>
                );
            }

            return (
                <Tooltip title={`This user's client is external and this role grants access to ${listLabels(capabilityLabels, 6)}. They have that access right now.`}>
                    <Chip icon={<WarningIcon />} label={`Exposed: ${primaryCapability}`} size="small" color="error" />
                </Tooltip>
            );
        }

        const mislabelledLabel = user.isDeleted
            ? 'Mislabelled (deleted account)'
            : !user.active
                ? 'Mislabelled (deactivated account)'
                : 'Mislabelled — external client on an Internal role';

        return (
            <Tooltip title={`This user's client is external and this role is typed Internal, but the role grants none of the sensitive capabilities this audit checks for${auditScopeSuffix}. Nothing is exposed by it — the assignment is still wrong and should be corrected.`}>
                <Chip label={mislabelledLabel} size="small" color="warning" variant="outlined" />
            </Tooltip>
        );
    };

    const renderBody = () => {
        if (loading) {
            return (
                <LoadingRow>
                    <CircularProgress />
                </LoadingRow>
            );
        }

        if (failed) {
            return (
                <ErrorState>
                    <ErrorOutlineIcon />
                    <Typography variant="body1">The list of users could not be loaded.</Typography>
                    <Typography variant="body2" color="text.secondary">
                        This is not a result — nothing has been checked. Try again, and if it keeps failing, do not treat this role as audited.
                    </Typography>
                    <Button variant="outlined" size="small" onClick={loadUsers}>Try again</Button>
                </ErrorState>
            );
        }

        if (users.length === 0) {
            return (
                <EmptyState>
                    <Typography variant="body1" color="text.secondary">
                        No users hold this role.
                    </Typography>
                </EmptyState>
            );
        }

        return (
            <>
                <RangeNote>
                    <Typography variant="caption" color="text.secondary">
                        Showing account{plural(users.length)} {start + 1}–{start + users.length} of {total}.
                        {flaggedTotal > 0 && ` Flagged accounts are ordered first, so they are rows 1–${flaggedTotal} of this list.`}
                    </Typography>
                </RangeNote>

                {renderBanner()}

                {roleChange && !roleChange.allowed && roleChange.reason && (
                    <BlockedNote severity="info">{roleChange.reason}</BlockedNote>
                )}

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell><strong>Email</strong></TableCell>
                                <TableCell><strong>Client</strong></TableCell>
                                <TableCell align="right"><strong>Status</strong></TableCell>
                                <TableCell align="right"><strong>Change role</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <UserRow key={user._id} tint={rowTint(user)}>
                                    <TableCell>
                                        <NameCell>
                                            <Typography variant="body2">
                                                {user.first_name} {user.last_name}
                                            </Typography>
                                            {renderFlagChip(user)}
                                        </NameCell>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {user.email}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {user.client?.name || '—'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {user.client ? (user.client.isInternal ? 'Internal' : 'External') : 'No client'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <StateChips>
                                            {user.isDeleted && <Chip label="Deleted" size="small" color="error" variant="outlined" />}
                                            {!user.active && !user.isDeleted && <Chip label="Inactive" size="small" variant="outlined" />}
                                            {user.active && !user.isDeleted && <Chip label="Active" size="small" color="success" variant="outlined" />}
                                        </StateChips>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={changeButtonTooltip(user)}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={!roleChange?.allowed || !user.canChangeRole || isSelf(user)}
                                                    onClick={() => openChangeDialog(user)}
                                                >
                                                    <SwapHorizIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </UserRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={total}
                    page={effectivePage}
                    onPageChange={(event, next) => setPage(next)}
                    rowsPerPage={limit}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    onRowsPerPageChange={(event) => {
                        setLimit(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                />
            </>
        );
    };

    const outcome = changeOutcome();

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogTitle>Users with the "{role?.name}" role</DialogTitle>
                <DialogContent>
                    <HelperText>
                        {data
                            ? `${total} account${plural(total)} hold this ${roleType} role — ${activeTotal} active, ${inactiveTotal} deactivated and ${deletedTotal} deleted. ${accessLine}`
                            : 'Everyone currently assigned this role, including deactivated and deleted accounts.'}
                    </HelperText>

                    {renderBody()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={changeOpen} onClose={() => !changing && setChangeOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Change this person's role</DialogTitle>
                <DialogContent>
                    <HelperText>
                        A role decides what someone can see and do in Pollux. This change takes effect immediately,
                        the next time they load a page.
                    </HelperText>

                    <ChangeBlock>
                        <ChangeFact>
                            <FactLabel variant="body2">Person</FactLabel>
                            <Typography variant="body2">{fullName(changeTarget)} ({changeTarget?.email})</Typography>
                        </ChangeFact>
                        <ChangeFact>
                            <FactLabel variant="body2">Client</FactLabel>
                            <Typography variant="body2">
                                {changeTarget?.client ? `${changeTarget.client.name} (${changeTarget.client.isInternal ? 'internal' : 'external'})` : 'No client on record'}
                            </Typography>
                        </ChangeFact>
                        <ChangeFact>
                            <FactLabel variant="body2">Current role</FactLabel>
                            <Typography variant="body2">"{role?.name}" ({roleType})</Typography>
                        </ChangeFact>
                    </ChangeBlock>

                    <FormControl fullWidth>
                        <InputLabel id="new-role-label">New role</InputLabel>
                        <Select
                            labelId="new-role-label"
                            value={newRoleId}
                            label="New role"
                            onChange={(event) => setNewRoleId(event.target.value)}
                        >
                            {assignableRoles.map((option) => (
                                <MenuItem key={option._id} value={option._id} disabled={!roleAllowedForHolder(option, changeTarget)}>
                                    {option.name} — {option.type === 'Internal' ? 'internal / staff' : 'client-facing'}
                                    {!roleAllowedForHolder(option, changeTarget) && ' — needs an internal client'}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <ChangeBlock>
                        <Typography variant="caption" color="text.secondary">
                            Only the roles you are allowed to assign are listed. A role with more privileges than your own
                            is not offered here, and the server rejects it if it is attempted.
                            {blockedForTarget && ` Internal roles are greyed out because ${changeTarget?.client ? `${changeTarget.client.name} is an external client` : `${fullName(changeTarget)} has no client on record`} — only someone at an internal client can hold one.`}
                        </Typography>
                    </ChangeBlock>

                    {selectedRole && (
                        <ChangeBlock>
                            <Typography variant="body2">{describeRole(selectedRole)}</Typography>
                        </ChangeBlock>
                    )}

                    {outcome && <Alert severity={outcome.severity}>{outcome.text}</Alert>}

                    {changeError && (
                        <ChangeBlock>
                            <Alert severity="error">{changeError}</Alert>
                        </ChangeBlock>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setChangeOpen(false)} disabled={changing}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmChange}
                        disabled={!newRoleId || changing}
                    >
                        {changing ? 'Changing role…' : `Change role to "${selectedRole?.name || '…'}"`}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default RoleUsersDialog;
